import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CURRENCY, QUOTE_CLASSIFICATION } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { setWorkflowSteps } from 'src/redux/auth.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { RootState } from 'src/redux/store';
import { getPricingRate } from 'src/services/CommonService';
import { createQuote } from 'src/services/QuoteService';
import { formatDecimalNumber } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

const convertCurrency = (value: number, fromCurrency: string, toCurrency: string, currencyRates: { cadRate: number; usdRate: number }): number => {
    if (!value || value === 0 || fromCurrency === toCurrency) {
        return formatDecimalNumber(value || 0, 2);
    }

    let convertedValue = 0;

    if (fromCurrency === CURRENCY.CAD && toCurrency === CURRENCY.USD) {
        convertedValue = value * (currencyRates.usdRate || 0.75);
    } else if (fromCurrency === CURRENCY.USD && toCurrency === CURRENCY.CAD) {
        convertedValue = value * (currencyRates.cadRate || 1);
    } else {
        convertedValue = value;
    }

    return formatDecimalNumber(convertedValue, 2);
};

import { consigneeFormKeys, shipperFormKeys } from '../../Modals/quoteDetailModal.const';
import { QUOTE_KEYS } from '../createQuote.const';
import {
    setCreateQuoteData,
    setCreateQuoteStep,
    setCompletedStep,
    resetCreateQuoteData,
    setIsCreateLoading,
    createQuoteData as createQuoteDataSelector,
    quoteDetails as quoteDetailsSelectorSelector,
    shipperConsigneeDetails as shipperConsigneeDetailsSelector,
    dimensionsDetails as dimensionsDetailsSelector,
    completedSteps as completedStepsSelector,
} from '../CreateQuote.slice';

import { createAddressPayload } from './Steps.helper';

export const useSaveNextStep = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const workFlowSteps = useAppSelector((state: RootState) => state.auth.workFlowSteps);
    const createQuoteData = useAppSelector(createQuoteDataSelector);
    const dimensionsDetails = useAppSelector(dimensionsDetailsSelector);
    const quoteDetails = useAppSelector(quoteDetailsSelectorSelector);
    const shipperConsigneeDetails = useAppSelector(shipperConsigneeDetailsSelector);
    const completedSteps = useAppSelector(completedStepsSelector);

    const createApiPayload = async (data: any, dimensionsDetailsOverride?: any) => {
        const shipperAddress = createAddressPayload(shipperConsigneeDetails?.shipperAddress, shipperFormKeys);
        const consigneeAddress = createAddressPayload(shipperConsigneeDetails?.consigneeAddress, consigneeFormKeys);
        // When called from FTL combined step, we pass dimensionsDetailsOverride directly because
        // the Redux closure value won't reflect the just-dispatched dimensionsDetails yet
        const effectiveDimensionsDetails = dimensionsDetailsOverride ?? dimensionsDetails;
        const additionalServices = effectiveDimensionsDetails?.additionalServices;

        let currencyRates = { cadRate: 1, usdRate: 0.75 };

        try {
            const response = await getPricingRate();

            if (response?.data) {
                currencyRates = {
                    cadRate: response.data.cadRate || 1,
                    usdRate: response.data.usdRate || 0.75,
                };
            }
        } catch (error) {
            console.error('Error fetching currency rates:', error);
        }

        let insuranceCommercialValueCAD = 0;
        let insuranceCommercialValueUSD = 0;

        if (data?.insuranceCommercialValue && data?.insuranceCurrencyType) {
            const numericValue = parseFloat(String(data.insuranceCommercialValue)) || 0;

            if (numericValue > 0) {
                const currentCurrency = data.insuranceCurrencyType || CURRENCY.CAD;

                insuranceCommercialValueCAD = currentCurrency === CURRENCY.CAD ? formatDecimalNumber(numericValue, 2) : convertCurrency(numericValue, currentCurrency, CURRENCY.CAD, currencyRates);

                insuranceCommercialValueUSD = currentCurrency === CURRENCY.USD ? formatDecimalNumber(numericValue, 2) : convertCurrency(numericValue, currentCurrency, CURRENCY.USD, currencyRates);
            }
        }

        const getDimensions = () => {
            if (!effectiveDimensionsDetails || effectiveDimensionsDetails?.dimensions?.length === 0 || quoteDetails?.classification === QUOTE_CLASSIFICATION?.FTL) {
                return [];
            }

            if (effectiveDimensionsDetails?.dimensions?.length === 1) {
                const dimension = effectiveDimensionsDetails?.dimensions[0];
                const hasValidValue =
                    (dimension.weight && dimension.weight >= 1) ||
                    (dimension.itemLength && dimension.itemLength >= 1) ||
                    (dimension.width && dimension.width >= 1) ||
                    (dimension.height && dimension.height >= 1) ||
                    (dimension.totalWeight && dimension.totalWeight >= 1);

                if (!hasValidValue) {
                    return [];
                }
            }

            return effectiveDimensionsDetails?.dimensions.map((dimension: any) => ({
                id: dimension.id,
                handlingUnit: dimension.handlingUnit,
                handlingUnitNo: dimension.handlingUnitNo,
                weight: dimension.weight,
                itemLength: dimension.itemLength,
                width: dimension.width,
                height: dimension.height,
                totalWeight: dimension.totalWeight,
                isStack: dimension.isStack,
                weightMeasurement: effectiveDimensionsDetails.weightMeasurement,
                lengthMeasurement: effectiveDimensionsDetails.lengthMeasurement,
                description: dimension.description,
                freightClass: dimension.freightClass,
                commodity: dimension.commodity,
                itemClassForDayross: dimension.itemClassForDayross,
            }));
        };

        return {
            address: {
                ...shipperAddress,
                ...consigneeAddress,
                shipperId: shipperConsigneeDetails?.shipperId,
                consigneeId: shipperConsigneeDetails?.consigneeId,
                customerMasterId: createQuoteData?.customerDetails?.id,
                divisionId: createQuoteData?.divisionId,
                customerBillingAccountId: createQuoteData?.customerBillingAccountId,
                hubCustomerId: createQuoteData?.hubSpotCustomerDetails?.hubCustomerId,
                hubCustomerName: createQuoteData?.hubSpotCustomerDetails?.hubCustomerName,
                ...quoteDetails,
                ...data,
                insuranceCommercialValue: insuranceCommercialValueCAD,
                insuranceCommercialValueUSD: insuranceCommercialValueUSD,
                linearFootage: effectiveDimensionsDetails?.linearFootage,
                totalWeight: effectiveDimensionsDetails?.totalWeight,
            },
            dimensions: getDimensions(),
            services: {
                serviceId: additionalServices?.serviceId?.join(','),

                //limited access
                limitedAccess: additionalServices?.limitedAccess,
                ...(additionalServices?.limitedAccess && {
                    limitedAccessType: additionalServices?.limitedAccessType,
                    limitedAccessOther: additionalServices?.limitedAccessOther,
                }),

                // inbond
                inbond: additionalServices?.inbond,
                ...(additionalServices?.inbond && {
                    inbondType: additionalServices?.inbondType,
                    inbondName: additionalServices?.inbondName,
                    inbondAddress: additionalServices?.inbondAddress,
                    inbondContactMethod: additionalServices?.inbondContactMethod,
                    inbondContactValue: additionalServices?.inbondContactValue,
                }),

                // dangerous goods
                dangerousGoods: additionalServices?.dangerousGoods,
                ...(additionalServices?.dangerousGoods && {
                    dangerGoodsType: additionalServices?.dangerGoodsType,
                    unitedNationsNumber: additionalServices?.unitedNationsNumber,
                    packagingGroup: additionalServices?.packagingGroup,
                    goodsClass: additionalServices?.goodsClass,
                    description: additionalServices?.description,
                    emergencyContactName: additionalServices?.emergencyContactName,
                    emergencyContactNumber: additionalServices?.emergencyContactNumber,
                    emergencyContactExt: additionalServices?.emergencyContactExt,
                    emergencyEapnbr: additionalServices?.emergencyEapnbr,
                }),
            },
        };
    };

    const onSaveNext = useCallback(
        (data: any, currentStep: string, localCompletedSteps?: string[]) => {
            switch (currentStep) {
                case QUOTE_KEYS.QUOTE_DETAILS:
                    dispatch(setCreateQuoteData({ data: { quoteDetails: data } }));

                    // If localCompletedSteps is provided (classification changed), reset completedSteps and clear dimensions
                    if (localCompletedSteps) {
                        dispatch(setCompletedStep([...localCompletedSteps, QUOTE_KEYS.QUOTE_DETAILS]));
                        dispatch(setCreateQuoteData({ data: { dimensionsDetails: null } }));
                    } else {
                        dispatch(setCompletedStep(QUOTE_KEYS.QUOTE_DETAILS));
                    }
                    dispatch(setCreateQuoteStep(QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS));
                    break;
                case QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS:
                    dispatch(setCreateQuoteData({ data: { shipperConsigneeDetails: data } }));
                    dispatch(setCompletedStep(QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS));
                    dispatch(setCreateQuoteStep(QUOTE_KEYS.SHIPMENT_DETAILS));
                    break;

                case QUOTE_KEYS.DIMENSIONS: {
                    dispatch(setCreateQuoteData({ data: { dimensionsDetails: data } }));
                    dispatch(setCompletedStep(QUOTE_KEYS.DIMENSIONS));

                    if (quoteDetails?.classification !== QUOTE_CLASSIFICATION.FTL) {
                        const LTL_REQUIRED_STEPS = [QUOTE_KEYS.QUOTE_DETAILS, QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS, QUOTE_KEYS.SHIPMENT_DETAILS];

                        for (const step of LTL_REQUIRED_STEPS) {
                            if (!completedSteps.includes(step)) {
                                dispatch(setCreateQuoteStep(step));

                                return;
                            }
                        }

                        dispatch(setIsCreateLoading(true));
                        createApiPayload(createQuoteData?.shipmentDetails ?? {}, data)
                            .then((payload) => {
                                createQuote(payload)
                                    .then((response: any) => {
                                        if (response?.data) {
                                            WalToast.success('Quote created');
                                            dispatch(setWorkflowSteps({ ...workFlowSteps, CREATE_FIRST_QUOTE: true }));
                                            dispatch(resetCreateQuoteData());
                                            const quoteId = response.data.data.quote.id;
                                            navigate(`${PATH.MY_QUOTE}/${quoteId}`, { replace: true, state: { isNewQuote: true } });

                                            return response.data;
                                        }
                                    })
                                    .catch((error: any) => {
                                        console.error('Error creating quote:', error);
                                        const errMsg = error?.response?.data?.message || 'Booking failed';
                                        WalToast.error(errMsg);
                                    })
                                    .finally(() => {
                                        dispatch(setIsCreateLoading(false));
                                    });
                            })
                            .catch((error: any) => {
                                console.error('Error building quote payload:', error);
                                dispatch(setIsCreateLoading(false));
                            });
                    } else {
                        dispatch(setCreateQuoteStep(QUOTE_KEYS.SHIPMENT_DETAILS));
                    }
                    break;
                }

                case QUOTE_KEYS.SHIPMENT_DETAILS: {
                    // LTL: SHIPMENT_DETAILS is step 3 — always save and proceed to DIMENSIONS
                    dispatch(setCreateQuoteData({ data: { shipmentDetails: data } }));
                    dispatch(setCompletedStep(QUOTE_KEYS.SHIPMENT_DETAILS));
                    dispatch(setCreateQuoteStep(QUOTE_KEYS.DIMENSIONS));
                    break;
                }
            }
        },
        [dispatch, completedSteps, navigate, workFlowSteps, createQuoteData, quoteDetails]
    );

    const onBack = useCallback(
        (data: any, currentStep: string) => {
            switch (currentStep) {
                case QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS:
                    dispatch(
                        setCreateQuoteData({
                            data: {
                                shipperConsigneeDetails: {
                                    shipperId: data.shipperId || null,
                                    shipperCompanyName: data.shipperCompanyName || '',
                                    shipperAddress: data.shipperAddress || null,
                                    consigneeId: data.consigneeId || null,
                                    consigneeCompanyName: data.consigneeCompanyName || '',
                                    consigneeAddress: data.consigneeAddress || null,
                                },
                            },
                        })
                    );
                    dispatch(setCreateQuoteStep(QUOTE_KEYS.QUOTE_DETAILS));
                    break;
                case QUOTE_KEYS.DIMENSIONS:
                    dispatch(setCreateQuoteData({ data: { dimensionsDetails: data } }));
                    dispatch(setCreateQuoteStep(QUOTE_KEYS.SHIPMENT_DETAILS));
                    break;
                case QUOTE_KEYS.SHIPMENT_DETAILS:
                    dispatch(setCreateQuoteData({ data: { shipmentDetails: data } }));
                    dispatch(setCreateQuoteStep(QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS));
                    break;
            }
        },
        [dispatch]
    );

    // Required steps that must be completed before the FTL combined step can create a quote.
    // DIMENSIONS is intentionally excluded because it is merged into the combined step itself.
    const FTL_REQUIRED_STEPS = [QUOTE_KEYS.QUOTE_DETAILS, QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS];

    // Used by FtlLoadShipmentDetails: saves both dimensions + shipment data in one shot.
    // Mirrors the SHIPMENT_DETAILS incomplete-step guard from the LTL flow:
    // if a prior step is not yet completed, saves data and navigates there instead of creating the quote.
    const onSaveFtlCombined = useCallback(
        async (dimensionsPayload: any, shipmentPayload: any) => {
            dispatch(setIsCreateLoading(true));
            dispatch(setCreateQuoteData({ data: { dimensionsDetails: dimensionsPayload } }));
            dispatch(setCompletedStep(QUOTE_KEYS.DIMENSIONS));
            dispatch(setCreateQuoteData({ data: { shipmentDetails: shipmentPayload } }));
            dispatch(setCompletedStep(QUOTE_KEYS.SHIPMENT_DETAILS));

            // If any required prior step is incomplete, navigate there (same logic as LTL SHIPMENT_DETAILS)
            for (const step of FTL_REQUIRED_STEPS) {
                if (!completedSteps.includes(step)) {
                    dispatch(setCreateQuoteStep(step));
                    dispatch(setIsCreateLoading(false));

                    return;
                }
            }

            createApiPayload(shipmentPayload, dimensionsPayload)
                .then((payload) => {
                    createQuote(payload)
                        .then((response: any) => {
                            if (response?.data) {
                                WalToast.success('Quote created');
                                dispatch(setWorkflowSteps({ ...workFlowSteps, CREATE_FIRST_QUOTE: true }));
                                dispatch(resetCreateQuoteData());
                                const quoteId = response.data.data.quote.id;
                                navigate(`${PATH.MY_QUOTE}/${quoteId}`, { replace: true, state: { isNewQuote: true } });

                                return response.data;
                            }
                        })
                        .catch((error: any) => {
                            console.error('Error creating quote:', error);
                            const errMsg = error?.response?.data?.message || 'Booking failed';
                            WalToast.error(errMsg);
                        })
                        .finally(() => {
                            dispatch(setIsCreateLoading(false));
                        });
                })
                .catch((error: any) => {
                    console.error('Error building quote payload:', error);
                    dispatch(setIsCreateLoading(false));
                });
        },
        [dispatch, navigate, workFlowSteps, completedSteps]
    );

    const onBackFtlCombined = useCallback(
        (dimensionsPayload: any, shipmentPayload: any) => {
            dispatch(setCreateQuoteData({ data: { dimensionsDetails: dimensionsPayload } }));
            dispatch(setCreateQuoteData({ data: { shipmentDetails: shipmentPayload } }));
            dispatch(setCreateQuoteStep(QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS));
        },
        [dispatch]
    );

    return { onSaveNext, onBack, createApiPayload, onSaveFtlCombined, onBackFtlCombined };
};
